
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  component?: string;
  data?: unknown;
  userId?: string;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private maxLogs = 500;
  private currentLevel = LogLevel.INFO;

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.currentLevel;
  }

  private addLog(level: LogLevel, message: string, component?: string, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const logEntry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      component,
      data,
      userId: this.getCurrentUserId()
    };

    this.logs.unshift(logEntry);
    
    // Ограничиваем размер массива
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Выводим в консоль браузера
    this.outputToConsole(logEntry);
  }

  private outputToConsole(entry: LogEntry): void {
    const prefix = `[${entry.component || 'App'}] ${entry.message}`;
    
    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(prefix, entry.data);
        break;
      case LogLevel.INFO:
        console.log(prefix, entry.data);
        break;
      case LogLevel.WARN:
        console.warn(prefix, entry.data);
        break;
      case LogLevel.ERROR:
        console.error(prefix, entry.data);
        break;
    }
  }

  private getCurrentUserId(): string | undefined {
    // Можно интегрировать с системой аутентификации
    return undefined;
  }

  debug(message: string, component?: string, data?: unknown): void {
    this.addLog(LogLevel.DEBUG, message, component, data);
  }

  info(message: string, component?: string, data?: unknown): void {
    this.addLog(LogLevel.INFO, message, component, data);
  }

  warn(message: string, component?: string, data?: unknown): void {
    this.addLog(LogLevel.WARN, message, component, data);
  }

  error(message: string, component?: string, data?: unknown): void {
    this.addLog(LogLevel.ERROR, message, component, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }
}

export const useLogger = (component?: string) => {
  const logger = Logger.getInstance();

  return {
    debug: (message: string, data?: unknown) => logger.debug(message, component, data),
    info: (message: string, data?: unknown) => logger.info(message, component, data),
    warn: (message: string, data?: unknown) => logger.warn(message, component, data),
    error: (message: string, data?: unknown) => logger.error(message, component, data),
    getLogs: logger.getLogs.bind(logger),
    clearLogs: logger.clearLogs.bind(logger),
    setLevel: logger.setLevel.bind(logger)
  };
};
